import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import z from 'zod'

import { zOrderFulfillmentStatus, type OrderListItem } from '@/api/admin-orders-types'
import { zOrderPaymentStatus } from '@/api/common-types'
import { preloadListOrders, useListOrders } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { SearchField } from '@/components/base/search-field'
import { OrderDetail, getOrderDetailHeight } from '@/components/table/details/order-detail'
import { FilterButton } from '@/components/table/filter-button'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { Table, useServerSort } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'

import { useOrderListColumnDefs } from './-components'
import { OrderAdvancedFilterDrawer } from './-order-advanced-filter-drawer'
export const Route = createFileRoute('/_authenticated/_general/orders/')({
	component: OrderList,
	validateSearch: z.object({
		limit: z.coerce.number().default(30),
		offset: z.coerce.number().default(0),
		q: z.string().optional(),
		sortBy: z.enum(['date', 'displayId', 'total']).optional(),
		sortOrder: z.enum(['asc', 'desc']).optional(),
		salesChannels: z.array(z.string()).optional(),
		fulfillmentStatuses: z.union([
			z.array(zOrderFulfillmentStatus),
			z
				.string()
				.transform(str => str.split(',').map(id => zOrderFulfillmentStatus.parse(id.trim())))
				.optional()
		]),
		paymentStatus: z.union([
			z.array(zOrderPaymentStatus),
			z
				.string()
				.transform(str => str.split(',').map(id => zOrderPaymentStatus.parse(id.trim())))
				.optional()
		]),
		minAmount: z.coerce.number().optional(),
		maxAmount: z.coerce.number().optional(),
		fromDate: z.coerce.number().optional(),
		toDate: z.coerce.number().optional()
	}),
	loaderDeps: ({
		search: {
			limit,
			offset,
			q,
			sortBy,
			sortOrder,
			salesChannels,
			paymentStatus,
			minAmount,
			maxAmount,
			fromDate,
			toDate
		}
	}) => ({
		limit,
		offset,
		q,
		sortBy,
		sortOrder,
		salesChannels,
		paymentStatus,
		minAmount,
		maxAmount,
		fromDate,
		toDate
	}),
	loader: async ({
		context,
		deps: {
			limit,
			offset,
			q,
			sortBy,
			sortOrder,
			salesChannels,
			paymentStatus,
			minAmount,
			maxAmount,
			fromDate,
			toDate
		}
	}) =>
		await preloadListOrders(context, {
			query: {
				limit,
				offset,
				q,
				sortBy,
				sortOrder,
				salesChannelUuids: salesChannels?.length ? salesChannels : undefined,
				paymentStatus: paymentStatus?.length ? paymentStatus : undefined,
				minAmount,
				maxAmount,
				fromDate,
				toDate
			}
		})
})

export function OrderList() {
	const { L } = useI18n()
	const search = Route.useSearch()
	const {
		limit = DEFAULT_PAGE_SIZE,
		offset,
		q,
		sortBy,
		sortOrder,
		salesChannels = [],
		paymentStatus = [],
		minAmount,
		maxAmount,
		fromDate,
		toDate
	} = search
	const navigate = useNavigate()
	const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false)

	const columnDefs = useOrderListColumnDefs()
	const serverSort = useServerSort(['displayId', 'date', 'total'])

	const orders = useListOrders({
		limit,
		offset,
		q,
		sortBy,
		sortOrder,
		salesChannelUuids: salesChannels.length > 0 ? salesChannels : undefined,
		paymentStatus: paymentStatus.length > 0 ? paymentStatus : undefined,
		minAmount,
		maxAmount,
		fromDate,
		toDate
	})

	const activeFilterCount =
		salesChannels.length +
		paymentStatus.length +
		[minAmount, maxAmount, fromDate, toDate].filter(value => value !== undefined).length

	return (
		<Container className='text flex flex-1 flex-col'>
			<ContainerHeader
				heading={L.Orders.Title()}
				rightSide={
					<div className='flex items-center gap-2'>
						<SearchField />
						<FilterButton onClick={() => setAdvancedFiltersOpen(true)} activeCount={activeFilterCount} />
					</div>
				}
			/>
			<Table<OrderListItem>
				className='w-full flex-1'
				data={orders.data?.orders}
				columnUtilities={{
					getCurrencyCode: ({ currencyCode }) => currencyCode
				}}
				columnDefs={columnDefs}
				serverSort={serverSort}
				animateRows
				suppressCellFocus
				getRowId={order => order.uuid}
				masterDetail
				detailRenderer={order => <OrderDetail order={order} />}
				detailPanelRowHeight={getOrderDetailHeight}
				rowToRouterConfig={order => ({
					to: '/orders/$orderUuid',
					params: { orderUuid: order.uuid }
				})}
			/>
			<div className='px-3 py-2 sm:px-6 sm:py-4'>
				<Pagination totalCount={orders.data?.count} />
			</div>
			<OrderAdvancedFilterDrawer
				open={advancedFiltersOpen}
				onOpenChange={setAdvancedFiltersOpen}
				defaultValues={{
					salesChannels: salesChannels ?? [],
					paymentStatus: paymentStatus ?? [],
					minAmount,
					maxAmount,
					fromDate,
					toDate
				}}
				onApply={filters => {
					navigate({
						search: {
							...search,
							...filters,
							salesChannels: filters.salesChannels.length > 0 ? filters.salesChannels : undefined,
							paymentStatus: filters.paymentStatus.length > 0 ? filters.paymentStatus : undefined,
							offset: 0
						}
					})
				}}
			/>
		</Container>
	)
}
